import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Breadcrumb } from "../Breadcrumb";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { ChevronLeft, Loader2, Mail, Phone, MapPin, User, Lock, Users, Plus } from "lucide-react";

import { toast } from "sonner";
import API from "../api/api01";

import { useNavigate } from "react-router-dom";
import { PATHS } from "../config/paths";
import { isValidEmail, isValidPhone, filterNumeric, isValidPassword } from "../utils/validation";

interface AddClientProps {}

export function AddClient({}: AddClientProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<{ id: string, name: string }[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    login_id: "",
    password: "",
    group_id: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    API.get("/superadmin/groups")
      .then((res) => {
        setGroups(res.data);
      })
      .catch(() => {
        toast.error("Failed to load groups");
      });
  }, []);

  const handleAdd = async () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.group_id) newErrors.group_id = "Please select a permission group";
    if (!formData.name.trim()) newErrors.name = "Full name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = "Please enter a valid business email address";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = "Exactly 10 digits required";
    }
    
    if (!formData.address.trim()) newErrors.address = "Office address is required";
    if (!formData.login_id.trim()) newErrors.login_id = "Login ID is required";
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Minimum 8 characters required";
    } else if (!isValidPassword(formData.password)) {
      newErrors.password = "Must include uppercase, lowercase, and a number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fix the errors in the form");
      return;
    }

    setErrors({});

    try {
      setLoading(true);

      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        login_id: formData.login_id,
        password: formData.password,
        group_id: formData.group_id,
      };

      await API.post("/superadmin/clients", payload);

      toast.success("Client added successfully!");
      navigate(PATHS.CLIENT_MANAGEMENT);

    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to add client");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="w-full max-w-6xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white leading-none">
              Add New Client
            </h1>


            <Breadcrumb
              items={[
                { label: "Manage Client" },
                { label: "Add Client" },
              ]}
              className="mt-2"
            />
          </div>

        </div>


        {/* CARD */}
        <Card className="border border-slate-200 dark:border-slate-800 shadow-sm rounded-lg bg-card">

          <CardHeader className="border-b border-slate-100 dark:border-slate-800/60 pb-6">
            <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">
              Client Information
            </CardTitle>

            <CardDescription className="text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-1">
              Fill in the details below to create a new client account.
            </CardDescription>
          </CardHeader>



          <form onSubmit={(e) => { e.preventDefault(); handleAdd(); }}>
            <CardContent className="space-y-6">

              <div className="space-y-2 pb-4 border-b">
                <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Assign Group (Permissions)<span className="text-red-500">*</span></Label>

                <div className="relative">
                  <Select
                    value={formData.group_id}
                    onValueChange={(val) => setFormData({ ...formData, group_id: val })}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a Group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {errors.group_id && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.group_id}</p>}
                <p className="text-[11px] font-medium text-muted-foreground mt-1">
                  The client will inherit the module permissions from this group.
                </p>

              </div>

              {/* NAME */}
              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Client Name <span className="text-red-500">*</span></Label>


                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                  <Input
                    placeholder="Enter client name"
                    className="pl-9 bg-background border-white/5"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </div>
                {errors.name && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.name}</p>}
              </div>

              {/* EMAIL */}
              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Email Address <span className="text-red-500">*</span></Label>


                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                  <Input
                    type="email"
                    placeholder="name@company.com"
                    className="pl-9 bg-background border-white/5"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </div>
                {errors.email && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.email}</p>}
              </div>

              {/* PHONE */}
              <div className="space-y-2">
                <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Phone Number <span className="text-red-500">*</span></Label>


                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                  <Input
                    placeholder="Enter phone number"
                    className="pl-9 bg-background border-white/5"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: filterNumeric(e.target.value) })
                    }
                  />
                </div>
                {errors.phone && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.phone}</p>}
              </div>

              {/* ADDRESS */}
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Office Address<span className="text-red-500">*</span></Label>


                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                  <Input
                    placeholder="Enter office address"
                    className="pl-9 bg-background border-slate-200 dark:border-slate-800"
                    value={formData.address}
                    onChange={(e) =>
                      setFormData({ ...formData, address: e.target.value })
                    }
                  />
                </div>
                {errors.address && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.address}</p>}
              </div>

              {/* DIVIDER */}
              <div className="border-t pt-4">
                <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground/70">
                  Login Credentials
                </p>

              </div>

              {/* LOGIN + PASSWORD */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80">Login ID <span className="text-red-500">*</span></Label>


                  <Input
                    placeholder="Enter login ID"
                    value={formData.login_id}
                    onChange={(e) =>
                      setFormData({ ...formData, login_id: e.target.value })
                    }
                  />
                  {errors.login_id && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.login_id}</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/80">Password <span className="text-red-500">*</span></Label>


                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

                    <Input
                      type="password"
                      placeholder="Enter password"
                      className="pl-9 bg-background border-white/5"
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                    />
                  </div>
                  {errors.password && <p className="text-[10px] font-bold text-red-500 uppercase mt-1">{errors.password}</p>}
                </div>

              </div>

            </CardContent>

            <CardFooter className="flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800/60 pt-6 mt-6">
              <Button
                type="button"
                variant="outline"
                className="rounded-lg border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-800/50 text-xs font-bold uppercase tracking-widest h-10 px-8 transition-all active:scale-95 shadow-sm"
                onClick={() => navigate(PATHS.CLIENT_MANAGEMENT)}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 text-white min-w-[160px] h-10 rounded-lg shadow-sm transition-all active:scale-95"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                <span className="text-[13px] font-bold uppercase tracking-widest">
                  {loading ? "Creating..." : "Add Client"}
                </span>
              </Button>
            </CardFooter>
          </form>

        </Card>
      </div>
    </div>


  );
}